import React, { useCallback } from "react";
import { Button } from "@mui/material";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import {
  HostSystemItemView,
  isAvailabilitySourceServiceValue,
} from "@tlc/ls2pac/dto/HostSystemItemView";
import { useEResourceAvailability } from "../../../../contexts/EResourceAvailabilityContext";
import { useDownloadableDialogs } from "./DownloadableDialogs";

interface DownloadableHoldCheckoutButtonProps {
  hold: HostSystemItemView;
}

export function DownloadableHoldCheckoutButton({
  hold,
}: DownloadableHoldCheckoutButtonProps) {
  const { openCheckoutDialog } = useDownloadableDialogs();
  const { getAvailabilityForEResource } = useEResourceAvailability();
  const [loadingAvailability, setLoadingAvailability] = React.useState(false);
  const { translate } = useLanguage();
  const onClick = useCallback(() => {
    // formatInfo is typically empty for non-arrived holds
    // but should be set for arrived. If not, we try to request it
    if (
      hold.formatInfo?.length &&
      isAvailabilitySourceServiceValue(hold.eresourceService) &&
      hold.eresourceId
    ) {
      // we already have formatInfo
      openCheckoutDialog({
        resource: hold.resource,
        source: {
          service: hold.eresourceService,
          itemId: hold.eresourceId,
          formatInfo: hold.formatInfo,
        },
      });
      return;
    }
    setLoadingAvailability(true);
    getAvailabilityForEResource(hold.resource?.hostBibliographicId)
      .then((availabilitySource) => {
        // if can checkout, show checkout dialog
        // bypass place hold checks and go straight to checkout dialog
        // if can't checkout or no availability source, show error toast
        // if not formatInfo show error
        openCheckoutDialog({
          resource: hold.resource,
          source: availabilitySource,
        });
        setLoadingAvailability(false);
      })
      .catch(() => {
        setLoadingAvailability(false);
      });
  }, [openCheckoutDialog, getAvailabilityForEResource, hold.resource]);

  return (
    <Button loading={loadingAvailability} onClick={onClick} variant="contained">
      {translate("checkout")}
    </Button>
  );
}
